import React, {useState, useEffect, useCallback} from 'react';
import axios from 'axios';
import './InstallateurDashboard.css';

const InstallateurDashboard = ({onLogout}) => {
    const [profile, setProfile] = useState(null);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    const API_URL = import.meta.env.VITE_API_URL;
    const token = localStorage.getItem('token');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [profileRes, clientsRes] = await Promise.all([
                // /me utilise le JWT pour identifier l'installateur connecté
                axios.get(`${API_URL}/installateurs/me`, {
                    headers: {Authorization: `Bearer ${token}`}
                }),
                axios.get(`${API_URL}/clients/mes-clients`, {
                    headers: {Authorization: `Bearer ${token}`}
                })
            ]);
            setProfile(profileRes.data);
            setClients(clientsRes.data.data || []);
        } catch (err) {
            setError('Erreur lors du chargement des données');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [API_URL, token]);

    useEffect(() => {fetchData();}, [fetchData]);

    // userId dérivé du profil après chargement
    const userId = profile?._id?.toString();

    const filtered = clients.filter(c =>
        `${c.prenom} ${c.nom} ${c.cin} ${c.telephone}`.toLowerCase().includes(search.toLowerCase())
    );

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

    const getInstallateurDossiers = (client) =>
        (client.dossiers || []).filter(d => {
            const instId = d.installateur?._id?.toString() || d.installateur?.toString();
            return instId === userId;
        });

    if (loading) return (
        <div className="inst-dash-wrapper">
            <div className="inst-dash-loading">
                <div className="inst-spinner" />
                <p>Chargement de votre tableau de bord...</p>
            </div>
        </div>
    );

    const totalInstalle = clients.reduce((acc, c) =>
        acc + getInstallateurDossiers(c).filter(d => d.statut === 'installe').length, 0);
    const totalNouveau = clients.reduce((acc, c) =>
        acc + getInstallateurDossiers(c).filter(d => d.statut === 'nouveau').length, 0);
    const totalReceptionne = clients.reduce((acc, c) =>
        acc + getInstallateurDossiers(c).filter(d => d.statut === 'receptionne').length, 0);

    return (
        <div className="inst-dash-wrapper">
            {/* Header */}
            <header className="inst-dash-header">
                <div className="inst-header-left">
                    <div className="inst-logo-badge"><span>⚡</span></div>
                    <div>
                        <h1 className="inst-header-title">SASTRA Energy</h1>
                        <p className="inst-header-sub">Espace Installateur</p>
                    </div>
                </div>
                <div className="inst-header-right">
                    {profile && (
                        <div className="inst-user-info">
                            <div className="inst-user-avatar">
                                {profile.prenom?.[0] || '?'}{profile.nom?.[0] || ''}
                            </div>
                            <div>
                                <p className="inst-user-name">{profile.prenom} {profile.nom}</p>
                                <p className="inst-user-role">Installateur</p>
                            </div>
                        </div>
                    )}
                    <button className="inst-logout-btn" onClick={onLogout}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        Déconnexion
                    </button>
                </div>
            </header>

            <main className="inst-dash-main">
                {error && <div className="inst-alert-error">{error}</div>}

                {/* Profile + Stats */}
                <div className="inst-stats-row">
                    <div className="inst-profile-card">
                        <div className="inst-profile-avatar">
                            {profile?.prenom?.[0] || '?'}{profile?.nom?.[0] || ''}
                        </div>
                        <h2 className="inst-profile-name">{profile?.prenom} {profile?.nom}</h2>
                        <p className="inst-profile-email">📧 {profile?.mail || '—'}</p>
                        <p className="inst-profile-phone">📱 {profile?.numeroTelephone || '—'}</p>

                    </div>

                    <div className="inst-stats-cards">
                        <div className="inst-stat-card inst-stat-blue">
                            <div className="inst-stat-icon">👥</div>
                            <div className="inst-stat-value">{profile?.nbrClients || clients.length}</div>
                            <div className="inst-stat-label">Total Clients</div>
                        </div>
                        <div className="inst-stat-card inst-stat-green">
                            <div className="inst-stat-icon">✅</div>
                            <div className="inst-stat-value">{totalInstalle}</div>
                            <div className="inst-stat-label">Installés</div>
                        </div>
                        <div className="inst-stat-card inst-stat-gold">
                            <div className="inst-stat-icon">🔄</div>
                            <div className="inst-stat-value">{totalNouveau}</div>
                            <div className="inst-stat-label">En cours</div>
                        </div>
                        <div className="inst-stat-card inst-stat-purple">
                            <div className="inst-stat-icon">🏆</div>
                            <div className="inst-stat-value">{totalReceptionne}</div>
                            <div className="inst-stat-label">Réceptionnés</div>
                        </div>
                    </div>
                </div>

                {/* Liste clients */}
                <div className="inst-clients-section">
                    <div className="inst-section-header">
                        <div>
                            <h2 className="inst-section-title">Mes Clients</h2>
                            <p className="inst-section-sub">
                                {filtered.length} client{filtered.length !== 1 ? 's' : ''} trouvé{filtered.length !== 1 ? 's' : ''}
                            </p>
                        </div>
                        <input
                            className="inst-search"
                            type="text"
                            placeholder="🔍 Rechercher un client..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>

                    {filtered.length === 0 ? (
                        <div className="inst-empty">
                            <div className="inst-empty-icon">📂</div>
                            <h3>Aucun client trouvé</h3>
                            <p>Vous n'avez pas encore de clients assignés.</p>
                        </div>
                    ) : (
                        <div className="inst-clients-grid">
                            {filtered.map(client => {
                                const myDossiers = getInstallateurDossiers(client);
                                const lastDossier = myDossiers[myDossiers.length - 1];
                                return (
                                    <div key={client._id} className="inst-client-card">
                                        <div className="inst-client-header">
                                            <div className="inst-client-avatar">
                                                {client.prenom?.[0] || '?'}{client.nom?.[0] || ''}
                                            </div>
                                            <div className="inst-client-name-block">
                                                <h3 className="inst-client-name">{client.prenom} {client.nom}</h3>
                                                <p className="inst-client-cin">CIN: {client.cin}</p>
                                            </div>
                                            {lastDossier && (
                                                <span className={`inst-status-badge inst-status-${lastDossier.statut}`}>
                                                    {lastDossier.statut === 'nouveau' ? 'En cours'
                                                        : lastDossier.statut === 'installe' ? 'Installé' : 'Réceptionné'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="inst-client-body">
                                            <div className="inst-client-row"><span>📱</span><span>{client.telephone || '—'}</span></div>
                                            <div className="inst-client-row"><span>📧</span><span>{client.email || '—'}</span></div>
                                            {lastDossier && (
                                                <>
                                                    <div className="inst-client-row">
                                                        <span>⚡</span><span>{lastDossier.puissance || 0} kWc</span>
                                                    </div>
                                                    <div className="inst-client-row">
                                                        <span>🏢</span>
                                                        <span>
                                                            {lastDossier.district
                                                                ? (typeof lastDossier.district === 'object'
                                                                    ? lastDossier.district.region
                                                                    : lastDossier.district)
                                                                : '—'}
                                                        </span>
                                                    </div>
                                                    <div className="inst-client-row">
                                                        <span>📅</span><span>{formatDate(lastDossier.dateArrivDossier)}</span>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="inst-client-footer">
                                            <span className="inst-dossiers-count">
                                                {myDossiers.length} dossier{myDossiers.length !== 1 ? 's' : ''}
                                            </span>
                                            <span className="inst-type-badge">{client.typeClient || 'BT'}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default InstallateurDashboard;
